import { useMemo } from "react"
import { useFormatters } from "@/hooks/use-formatters"
import { type RevenueRecognitionSchedule, type RevenueRecognitionPeriod, REVENUE_RECOGNITION_METHOD, DEFAULT_REVENUE_RECOGNITION_METHOD, type RevenueRecognitionMethod } from "@/types/ui/revenue-recognition"

/**
 * Normalizes a date to UTC midnight for accurate calendar day calculations
 * Eliminates DST and time-of-day effects
 */
function toUtcMidnight(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * Calculates the number of days between two dates (inclusive)
 */
function calculateDaysBetween(startDate: Date, endDate: Date): number {
  const startUtc = toUtcMidnight(startDate)
  const endUtc = toUtcMidnight(endDate)
  return Math.floor((endUtc - startUtc) / (1000 * 60 * 60 * 24)) + 1
}

/**
 * Converts a percentage to a dollar amount based on total contract value
 * @param percentage - The percentage value (e.g., 25 for 25%)
 * @param totalContractValue - The total contract value
 * @returns The dollar amount rounded to 2 decimal places
 */
export function percentToAmount(percentage: number | null, totalContractValue: number): number | null {
  if (percentage === null || percentage === undefined) return null
  if (totalContractValue <= 0) return null
  const amount = (percentage / 100) * totalContractValue
  return Math.round(amount * 100) / 100
}

/**
 * Converts a dollar amount to a percentage based on total contract value
 * @param amount - The dollar amount
 * @param totalContractValue - The total contract value
 * @returns The percentage value rounded to 2 decimal places
 */
export function amountToPercent(amount: number | null, totalContractValue: number): number | null {
  if (amount === null || amount === undefined) return null
  if (totalContractValue <= 0) return null
  const percentage = (amount / totalContractValue) * 100
  return Math.round(percentage * 100) / 100
}

/**
 * Hook for calculating revenue recognition schedules
 * Uses the application's formatters for proper date formatting
 */
export function useRevenueRecognitionCalculator() {
  const { formatDate, formatMonthYear } = useFormatters()

  /**
   * @param calculationMethod - Method for distributing revenue: 'straight-line' (equal per period) or 'weighted' (proportional to days)
   * @param lockedPeriodAmounts - Optional map of period IDs to locked amounts. When provided,
   *   locked periods keep their amounts and remaining TCV is distributed to unlocked periods.
   */
  const calculateSchedule = useMemo(() => (
    totalContractValue: number,
    projectStartDate: Date,
    projectEndDate: Date,
    calculationMethod: RevenueRecognitionMethod = DEFAULT_REVENUE_RECOGNITION_METHOD,
    lockedPeriodAmounts?: Record<string, number>
  ): RevenueRecognitionSchedule => {
    
    // Generate periods
    const periods: RevenueRecognitionPeriod[] = []
    let currentDate = new Date(projectStartDate)
    
    while (currentDate <= projectEndDate) {
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth()
      
      // Calculate period start and end
      const monthEnd = new Date(year, month + 1, 0)
      
      const periodStart = new Date(currentDate)
      const periodEnd = projectEndDate < monthEnd ? new Date(projectEndDate) : monthEnd
      // Create period ID using first day of month (e.g., "2024-01-01")
      const periodId = `${year}-${String(month + 1).padStart(2, '0')}-01`
      
      // Create period label using proper formatters
      // If entire period is within same month, show "Jan 2024"
      // If period spans multiple months, show date range "Jan 15 - Feb 28, 2024"
      const periodLabel = periodStart.getMonth() === periodEnd.getMonth()
        ? formatMonthYear(periodStart)
        : `${formatDate(periodStart)} - ${formatDate(periodEnd)}`
      
      periods.push({
        id: periodId,
        periodLabel,
        periodStartDate: periodStart,
        periodEndDate: periodEnd,
        systemAmount: 0,
        systemPercentage: 0,
        statusCode: 'pending',
      })
      
      currentDate = new Date(year, month + 1, 1)
    }
    
    // Calculate amounts based on whether we have locked periods
    const lockedTotal = lockedPeriodAmounts 
      ? Object.values(lockedPeriodAmounts).reduce((sum, amt) => sum + amt, 0) 
      : 0
    const remainingAmount = totalContractValue - lockedTotal
    
    // Second pass: calculate system amounts
    let systemTotal = 0
    let lastUnlockedIndex = -1
    
    // Calculate the rate based on method and create calculation function
    let calculateUnlockedAmount: (period: RevenueRecognitionPeriod) => number
    
    if (calculationMethod === REVENUE_RECOGNITION_METHOD.STRAIGHT_LINE) {
      // Straight-line: Equal distribution across unlocked periods
      const unlockedPeriodCount = periods
        .filter(p => !lockedPeriodAmounts || !(p.id in lockedPeriodAmounts))
        .length
      
      const monthlyRate = unlockedPeriodCount > 0 ? remainingAmount / unlockedPeriodCount : 0
      calculateUnlockedAmount = () => monthlyRate
    } else {
      // Weighted: Distribution proportional to days in each period
      const unlockedDays = periods
        .filter(p => !lockedPeriodAmounts || !(p.id in lockedPeriodAmounts))
        .reduce((sum, p) => sum + calculateDaysBetween(p.periodStartDate, p.periodEndDate), 0)
      
      const dailyRate = unlockedDays > 0 ? remainingAmount / unlockedDays : 0
      calculateUnlockedAmount = (period) => dailyRate * calculateDaysBetween(period.periodStartDate, period.periodEndDate)
    }
    
    // Apply calculation to all periods (common logic extracted)
    periods.forEach((period, index) => {
      const isLocked = lockedPeriodAmounts && period.id in lockedPeriodAmounts
      
      if (isLocked) {
        period.systemAmount = lockedPeriodAmounts[period.id]
      } else {
        period.systemAmount = Math.round(calculateUnlockedAmount(period) * 100) / 100
        lastUnlockedIndex = index
      }
      
      period.systemPercentage = totalContractValue > 0 
        ? Math.round((period.systemAmount / totalContractValue) * 100 * 100) / 100 
        : 0
      systemTotal += period.systemAmount
    })
    
    // Adjust last unlocked period for rounding differences
    if (lastUnlockedIndex >= 0 && Math.abs(systemTotal - totalContractValue) > 0.005) {
      const lastPeriod = periods[lastUnlockedIndex]
      const difference = totalContractValue - systemTotal
      lastPeriod.systemAmount = Math.round((lastPeriod.systemAmount + difference) * 100) / 100
      lastPeriod.systemPercentage = totalContractValue > 0 
        ? Math.round((lastPeriod.systemAmount / totalContractValue) * 100 * 100) / 100 
        : 0
    }
    
    return {
      periods,
      totalSystemAmount: totalContractValue,
      totalSystemPercentage: 100,
    }
  }, [formatDate, formatMonthYear])

  return {
    calculateSchedule,
    percentToAmount,
    amountToPercent,
  }
}

